import type {
  EnrollmentResponse,
  Participant,
  ParticipantIssuance,
} from "@/redux/certifications/types";
import type {
  BanState,
  ParticipantStatus,
} from "@/domains/certifications/types";
import {
  BAN_KINDS,
  ENROLLMENT_STATES,
  ISSUANCE_STATES,
  PARTICIPANT_STATUSES,
} from "@/domains/certifications/constants";

const ISSUING_STATES: ParticipantIssuance["state"][] = [
  ISSUANCE_STATES.QUEUED,
  ISSUANCE_STATES.ISSUING,
];

export const getPathCredential = (
  participant: Pick<Participant, "credentials" | "selectedTrack">,
) =>
  participant.credentials.find(
    (credential) => credential.track === participant.selectedTrack,
  ) ?? null;

export const getEnrollmentCredential = (
  enrollment: EnrollmentResponse | Participant,
) =>
  "credentials" in enrollment
    ? getPathCredential(enrollment)
    : enrollment.credential;

export const getBanState = (
  participant: Pick<Participant, "isBanned" | "banEndsAt">,
): BanState | null => {
  if (!participant.isBanned) {
    return null;
  }
  return participant.banEndsAt
    ? { kind: BAN_KINDS.TIMED, endsAt: participant.banEndsAt }
    : { kind: BAN_KINDS.PERMANENT };
};

export const deriveParticipantStatus = (
  participant: Participant,
  weekCount: number,
): ParticipantStatus => {
  if (participant.state === ENROLLMENT_STATES.WITHDRAWN) {
    return getBanState(participant)?.kind === BAN_KINDS.PERMANENT
      ? PARTICIPANT_STATUSES.WITHDRAWN_BAN
      : PARTICIPANT_STATUSES.WITHDRAWN;
  }
  const credential = getPathCredential(participant);
  if (credential?.state === ISSUANCE_STATES.ISSUED) {
    return PARTICIPANT_STATUSES.CERTIFIED;
  }
  const isEveryWeekComplete =
    weekCount > 0 && participant.weeksCompleted >= weekCount;
  if (
    isEveryWeekComplete ||
    (credential && ISSUING_STATES.includes(credential.state))
  ) {
    return PARTICIPANT_STATUSES.AWAITING_ISSUE;
  }
  return participant.weeksCompleted > 0 || participant.satisfiedCount > 0
    ? PARTICIPANT_STATUSES.IN_PROGRESS
    : PARTICIPANT_STATUSES.NOT_STARTED;
};
